import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './EdgeCredentials.css';
import {
  Search, X, ChevronDown, ChevronRight, Download, KeyRound,
  BookOpen, Trash2, Loader2,
} from 'lucide-react';
import { useToast } from '../components/Toast';
import Loading from '../components/Loading';
import timeAgo from '../utils/timeAgo';
import io from 'socket.io-client';

const EdgeCredentials = () => {
  const location = useLocation();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [showNotReserved, setShowNotReserved] = useState(false);
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [reserveModal, setReserveModal] = useState(null);
  const [reserveCluster, setReserveCluster] = useState('');
  const [reserveCloud, setReserveCloud] = useState('gcp');
  const [reserveCount, setReserveCount] = useState(1);
  const [clusters, setClusters] = useState([]);

  // Inline operation state — tracks which cluster row is active
  const [activeOp, setActiveOp] = useState(null);   // { cluster, type: 'reserve'|'release' }
  const [opLogs, setOpLogs] = useState([]);
  const [showInlineLog, setShowInlineLog] = useState(false);
  const [selectedAddrs, setSelectedAddrs] = useState([]);  // checked addresses for release
  const [checkingStatus, setCheckingStatus] = useState(false); // true while checking peer status
  const [releaseConfirm, setReleaseConfirm] = useState(null); // { customer, cloud, addresses, activeAddrs }

  const { showToast } = useToast();
  const logEndRef = useRef(null);
  const backend_url = process.env.REACT_APP_BACKEND_URL;

  // ── WebSocket for live log streaming ──────────────────────────────────
  useEffect(() => {
    const socket = io(backend_url);
    socket.on('deployment-update', (data) => {
      if (data.log) {
        setOpLogs((prev) => [...prev.slice(-200), data.log]);
      }
    });
    return () => socket.disconnect();
  }, [backend_url]);

  useEffect(() => {
    if (logEndRef.current && showInlineLog) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [opLogs, showInlineLog]);

  // ── Fetch kubeadm clusters from DB ────────────────────────────────────
  const fetchClusters = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backend_url}/api/clusters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const kubeadm = (res.ok && Array.isArray(data) ? data : []).filter(
        (c) => c.deployment_type === 'kubeadm_hub' && c.state !== 'Deleted' && c.state !== 'Mark for deletion'
      );
      setClusters(kubeadm);
    } catch (err) {
      console.error('Error fetching clusters:', err);
    }
  }, [backend_url]);

  // ── Fetch reservations ────────────────────────────────────────────────
  const fetchReservations = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backend_url}/api/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setReservations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching reservations:', err);
    } finally {
      setLoading(false);
    }
  }, [backend_url]);

  useEffect(() => {
    fetchClusters();
    fetchReservations();
  }, [fetchClusters, fetchReservations]);

  // ── Reserve ───────────────────────────────────────────────────────────
  const doReserve = useCallback(async (customerName, hubCloud, count) => {
    setActiveOp({ cluster: customerName, type: 'reserve' });
    setOpLogs([]);
    setShowInlineLog(false);
    showToast(`Reserving ${count} credential(s) for ${customerName}...`, 'Success');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backend_url}/api/credentials/reserve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ customerName, hubCloud, count }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reserve failed');
      showToast(`Credentials reserved for ${customerName}`, 'Success');
      await fetchReservations();
      setExpandedCluster(customerName);
    } catch (err) {
      showToast(err.message, 'Fail');
    } finally {
      setActiveOp(null);
    }
  }, [backend_url, showToast, fetchReservations]);

  const openReserveModal = (clusterName, cloud) => {
    setReserveCluster(clusterName || '');
    setReserveCloud(cloud || 'gcp');
    setReserveCount(1);
    setReserveModal(true);
  };

  const submitReserve = async () => {
    setReserveModal(null);
    await doReserve(reserveCluster, reserveCloud, reserveCount);
  };

  // Auto-trigger reserve when navigated from ClusterList with reserveAction
  const pendingReserve = useRef(false);
  useEffect(() => {
    const action = location.state?.reserveAction;
    if (action && !pendingReserve.current) {
      pendingReserve.current = true;
      // Show unreserved section so the user sees the "Reserving..." status
      setShowNotReserved(true);
      // Clear the navigation state so refreshing doesn't re-trigger
      window.history.replaceState({}, '');
      doReserve(action.customerName, action.hubCloud, action.count);
    }
  }, [location.state, doReserve]);

  // ── Release addresses — check status first, confirm if active ────────
  const handleRelease = async (customerName, hubCloud, addresses) => {
    if (!addresses.length) return;

    // Check peer status before releasing
    setCheckingStatus(true);
    try {
      const token = localStorage.getItem('authToken');
      const statusRes = await fetch(`${backend_url}/api/credentials/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ customerName, hubCloud, addresses }),
      });
      const statusData = await statusRes.json();
      const activeAddrs = (statusData.addresses || [])
        .filter((a) => a.active)
        .map((a) => a.address);

      if (activeAddrs.length > 0) {
        setReleaseConfirm({ customer: customerName, cloud: hubCloud, addresses, activeAddrs });
        return;
      }
    } catch {
      setReleaseConfirm({
        customer: customerName, cloud: hubCloud, addresses,
        activeAddrs: null,
      });
      return;
    } finally {
      setCheckingStatus(false);
    }

    // No active addresses — proceed directly
    await doRelease(customerName, hubCloud, addresses);
  };

  const doRelease = async (customerName, hubCloud, addresses) => {
    setActiveOp({ cluster: customerName, type: 'release' });
    setOpLogs([]);
    setShowInlineLog(false);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backend_url}/api/credentials/release`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customerName,
          hubCloud,
          addresses,
          force: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Release failed');
      showToast(data.message, 'Success');
      setSelectedAddrs([]);
      setExpandedCluster(null);
      await fetchReservations();
    } catch (err) {
      showToast(err.message, 'Fail');
    } finally {
      setActiveOp(null);
    }
  };

  const toggleAddr = (addr) => {
    setSelectedAddrs((prev) =>
      prev.includes(addr) ? prev.filter((a) => a !== addr) : [...prev, addr]
    );
  };

  const toggleAllAddrs = (addresses) => {
    const allSelected = addresses.every((a) => selectedAddrs.includes(a));
    setSelectedAddrs(allSelected ? [] : addresses.map((a) => a));
  };

  // ── Download bundle ───────────────────────────────────────────────────
  const handleDownload = async (customerName, cloud) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${backend_url}/api/credentials/download/${customerName}?hubCloud=${cloud}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${customerName}-edge-bundle.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Edge bundle downloaded', 'Success');
    } catch (err) {
      showToast(err.message, 'Fail');
    }
  };

  // ── Group reservations by cluster ─────────────────────────────────────
  const grouped = {};
  for (const r of reservations) {
    if (!grouped[r.customer]) {
      grouped[r.customer] = { cloud: r.hubCloud, addresses: [] };
    }
    grouped[r.customer].addresses.push(r);
  }

  // Build display rows: clusters with reservations + optionally unreserved
  const reservedClusters = Object.keys(grouped);
  const unreservedClusters = clusters.filter((c) => !grouped[c.name]);

  // Filter by search
  const filteredReserved = reservedClusters.filter(
    (name) => !searchName || name.toLowerCase().includes(searchName.toLowerCase())
  );
  const filteredUnreserved = unreservedClusters.filter(
    (c) => !searchName || c.name.toLowerCase().includes(searchName.toLowerCase())
  );

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="edge-creds-container">
      <h2>Edge Credentials</h2>

      {/* ── Filter Bar ── */}
      {!loading && (
        <div className="edge-filter-bar">
          <div className="filter-search">
            <Search size={16} className="filter-search-icon" />
            <input
              type="text"
              placeholder="Search clusters..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
            {searchName && (
              <X
                size={14}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1', cursor: 'pointer' }}
                onClick={() => setSearchName('')}
              />
            )}
          </div>
          <label className="edge-filter-toggle">
            <input
              type="checkbox"
              checked={showNotReserved}
              onChange={(e) => setShowNotReserved(e.target.checked)}
            />
            Show not reserved
          </label>
          <span className="edge-filter-count">
            {filteredReserved.length} cluster{filteredReserved.length !== 1 ? 's' : ''} with credentials
            {showNotReserved && unreservedClusters.length > 0 && (
              <> &middot; {filteredUnreserved.length} without</>
            )}
          </span>
          <button className="edge-btn" onClick={() => setGuideOpen(true)}>
            <BookOpen size={14} /> Setup Guide
          </button>
        </div>
      )}

      {/* ── Cluster Table ── */}
      {loading ? (
        <Loading type="skeleton" rows={4} height={52} />
      ) : (
        <table className="edge-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Cluster</th>
              <th>Cloud</th>
              <th>Credentials</th>
              <th>Last Reserved</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReserved.length === 0 && (!showNotReserved || filteredUnreserved.length === 0) && (
              <tr>
                <td colSpan={6} className="edge-no-results">
                  {reservations.length === 0
                    ? 'No edge credentials reserved yet. Toggle "Show not reserved" to see available clusters.'
                    : 'No clusters match the current search.'}
                </td>
              </tr>
            )}

            {/* ── Clusters WITH reservations ── */}
            {filteredReserved.map((name) => {
              const group = grouped[name];
              const isExpanded = expandedCluster === name;
              const isActive = activeOp?.cluster === name;
              const latestTime = group.addresses.reduce(
                (latest, r) => (r.reservedAt > latest ? r.reservedAt : latest), ''
              );

              return (
                <React.Fragment key={name}>
                  <tr
                    className={`edge-main-row ${isExpanded ? 'expanded' : ''} ${isActive ? 'operating' : ''}`}
                    onClick={() => { if (!isActive) { setExpandedCluster(isExpanded ? null : name); setSelectedAddrs([]); } }}
                  >
                    <td className="edge-expand-cell">
                      {isActive
                        ? <Loader2 size={16} className="edge-row-spinner" />
                        : isExpanded
                          ? <ChevronDown size={18} className="edge-expand-icon" />
                          : <ChevronRight size={18} className="edge-expand-icon" />}
                    </td>
                    <td className="edge-cluster-name">{name}</td>
                    <td>{(group.cloud || '').toUpperCase()}</td>
                    <td>
                      <span className="edge-count-badge">
                        {group.addresses.length} address{group.addresses.length !== 1 ? 'es' : ''}
                      </span>
                    </td>
                    <td className="edge-time">{latestTime ? timeAgo(latestTime) : '\u2014'}</td>
                    <td className="edge-actions-cell" onClick={(e) => e.stopPropagation()}>
                      {isActive ? (
                        <span className="edge-inline-status">
                          {activeOp.type === 'reserve' ? 'Reserving...' : 'Releasing...'}
                          <button
                            className="edge-inline-log-toggle"
                            onClick={() => setShowInlineLog((v) => !v)}
                          >
                            {showInlineLog ? 'Hide log' : 'Show log'}
                          </button>
                        </span>
                      ) : (
                        <>
                          <button className="edge-btn" onClick={() => handleDownload(name, group.cloud)} disabled={!!activeOp}>
                            <Download size={13} /> Bundle
                          </button>
                          <button className="edge-btn" onClick={() => openReserveModal(name, group.cloud)} disabled={!!activeOp}>
                            <KeyRound size={13} /> Reserve
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {/* ── Inline log row (only for active operation) ── */}
                  {isActive && showInlineLog && (
                    <tr className="edge-inline-log-row">
                      <td colSpan={6}>
                        <div className="edge-inline-log-body">
                          {opLogs.length === 0 && (
                            <span className="edge-log-empty">Waiting for output...</span>
                          )}
                          {opLogs.map((line, i) => (
                            <div key={i} className={`log-line${line.toLowerCase().includes('error') || line.toLowerCase().includes('failed') ? ' error' : ''}`}>
                              {line}
                            </div>
                          ))}
                          <div ref={logEndRef} />
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* ── Expanded: individual addresses with checkboxes ── */}
                  {!isActive && isExpanded && (
                    <tr className="edge-detail-row">
                      <td colSpan={6}>
                        <div className="edge-detail-grid">
                          <div className="edge-detail-header">
                            <span className="edge-detail-check-col">
                              <input
                                type="checkbox"
                                checked={group.addresses.length > 0 && group.addresses.every((a) => selectedAddrs.includes(a.address))}
                                onChange={() => toggleAllAddrs(group.addresses.map((a) => a.address))}
                              />
                            </span>
                            <span>Address</span>
                            <span>Reserved</span>
                          </div>
                          {group.addresses.map((r) => (
                            <div key={r.address} className="edge-detail-item">
                              <span className="edge-detail-check-col">
                                <input
                                  type="checkbox"
                                  checked={selectedAddrs.includes(r.address)}
                                  onChange={() => toggleAddr(r.address)}
                                />
                              </span>
                              <span className="edge-address">{r.address}</span>
                              <span className="edge-time">{r.reservedAt ? timeAgo(r.reservedAt) : '\u2014'}</span>
                            </div>
                          ))}
                          {selectedAddrs.length > 0 && (
                            <div className="edge-detail-actions">
                              <button
                                className="edge-btn danger"
                                disabled={!!activeOp || checkingStatus}
                                onClick={() => handleRelease(name, group.cloud, selectedAddrs)}
                              >
                                {checkingStatus ? (
                                  <><Loader2 size={13} className="edge-row-spinner" /> Checking connections...</>
                                ) : (
                                  <><Trash2 size={13} /> Release {selectedAddrs.length} selected</>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* ── Clusters WITHOUT reservations (when filter active) ── */}
            {showNotReserved && filteredUnreserved.map((c) => {
              const isActive = activeOp?.cluster === c.name;
              return (
                <React.Fragment key={c.name}>
                  <tr className={`edge-main-row edge-unreserved-row ${isActive ? 'operating' : ''}`}>
                    <td className="edge-expand-cell">
                      {isActive && <Loader2 size={16} className="edge-row-spinner" />}
                    </td>
                    <td className="edge-cluster-name">{c.name}</td>
                    <td>{(c.cloud || 'gcp').toUpperCase()}</td>
                    <td>
                      <span className="edge-count-badge empty">No credentials</span>
                    </td>
                    <td className="edge-time">{'\u2014'}</td>
                    <td className="edge-actions-cell">
                      {isActive ? (
                        <span className="edge-inline-status">
                          Reserving...
                          <button
                            className="edge-inline-log-toggle"
                            onClick={() => setShowInlineLog((v) => !v)}
                          >
                            {showInlineLog ? 'Hide log' : 'Show log'}
                          </button>
                        </span>
                      ) : (
                        <button className="edge-btn primary" onClick={() => openReserveModal(c.name, c.cloud)} disabled={!!activeOp}>
                          <KeyRound size={13} /> Reserve
                        </button>
                      )}
                    </td>
                  </tr>

                  {/* ── Inline log row for unreserved cluster ── */}
                  {isActive && showInlineLog && (
                    <tr className="edge-inline-log-row">
                      <td colSpan={6}>
                        <div className="edge-inline-log-body">
                          {opLogs.length === 0 && (
                            <span className="edge-log-empty">Waiting for output...</span>
                          )}
                          {opLogs.map((line, i) => (
                            <div key={i} className={`log-line${line.toLowerCase().includes('error') || line.toLowerCase().includes('failed') ? ' error' : ''}`}>
                              {line}
                            </div>
                          ))}
                          <div ref={logEndRef} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── Setup Guide Slide Panel ── */}
      {guideOpen && (
        <>
          <div className="edge-guide-backdrop" onClick={() => setGuideOpen(false)} />
          <div className="edge-guide-panel">
            <div className="edge-guide-panel-header">
              <h3><BookOpen size={16} /> Edge Node Setup Guide</h3>
              <button className="edge-guide-close" onClick={() => setGuideOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="edge-guide-body">
              <h4>VM Requirements</h4>
              <dl className="edge-guide-network">
                <dt>OS</dt>
                <dd>Ubuntu 24.04 LTS (or RHEL 9.x)</dd>
                <dt>CPU</dt>
                <dd>16 cores recommended (minimum 8)</dd>
                <dt>Memory</dt>
                <dd>64 GB recommended (minimum 32 GB)</dd>
                <dt>Disk</dt>
                <dd>Minimum 100 GB root disk</dd>
              </dl>

              <h4>Network Requirements</h4>
              <dl className="edge-guide-network">
                <dt>TCP :51821</dt>
                <dd>Allow outbound TCP to {'<gateway IP>'}:51821 (provided after hub deployment)</dd>
                <dt>Internet</dt>
                <dd>Outbound internet access (for package downloads during install)</dd>
              </dl>
              <p style={{ marginTop: 8, fontSize: '0.85rem' }}>
                Edge VM network must <strong>not</strong> overlap with:
              </p>
              <dl className="edge-guide-network">
                <dt>10.8.0.0/24</dt>
                <dd>WireGuard VPN addresses</dd>
                <dt>10.100.0.0/16</dt>
                <dd>Hub VPC (internal)</dd>
                <dt>10.200.0.0/16</dt>
                <dd>Kubernetes Service CIDR</dd>
                <dt>10.244.0.0/16</dt>
                <dd>Kubernetes Pod CIDR</dd>
              </dl>

              <h4>Operator Steps (Part 1)</h4>
              <ol>
                <li>Reserve credentials from the table or from Cluster Overview action menu</li>
                <li>Click <strong>Download Bundle</strong> to get the tar with:
                  <ul>
                    <li><code>lightbeam-bootstrap.tar.gz</code> — install scripts (same for all VMs)</li>
                    <li><code>edge-config-*.env</code> — one per VM (unique, don't mix)</li>
                    <li><code>SETUP-INSTRUCTIONS.txt</code> — send to customer</li>
                  </ul>
                </li>
                <li>Send the bundle to the customer</li>
              </ol>

              <h4>Customer Steps (Part 2)</h4>
              <ol>
                <li>Extract bootstrap scripts on each VM</li>
                <li>Copy the correct <code>.env</code> file (one per VM, renamed to <code>kubeadm-edge.env</code>)</li>
                <li>Run <code>installer_common.sh</code> (packages)</li>
                <li>Run <code>edge.sh</code> (VPN + kubeadm join)</li>
              </ol>

              <div className="edge-guide-warning">
                Token Expiry: kubeadm join token expires in 24h. If customer can't run setup in time,
                release and re-reserve to get fresh credentials.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Reserve Modal ── */}
      {reserveModal && (
        <div className="modal-overlay" onClick={() => !activeOp && setReserveModal(null)}>
          <div className="edge-reserve-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reserve Edge Credentials</h3>

            <div className="edge-reserve-field">
              <label>Cluster</label>
              {reserveCluster ? (
                <input type="text" value={reserveCluster} readOnly />
              ) : (
                <select value={reserveCluster} onChange={(e) => {
                  const c = clusters.find((cl) => cl.name === e.target.value);
                  setReserveCluster(e.target.value);
                  if (c) { setReserveCloud(c.cloud || 'gcp'); }
                }}>
                  <option value="">Select cluster...</option>
                  {clusters.map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="edge-reserve-field">
              <label>Cloud</label>
              <input type="text" value={reserveCloud.toUpperCase()} readOnly />
            </div>

            <div className="edge-reserve-field">
              <label>Number of VMs</label>
              <input
                type="number"
                min={1}
                max={20}
                value={reserveCount}
                onChange={(e) => setReserveCount(parseInt(e.target.value) || 1)}
              />
            </div>

            <div className="edge-reserve-actions">
              <button className="edge-btn" onClick={() => setReserveModal(null)}>
                Cancel
              </button>
              <button
                className="edge-btn primary"
                onClick={submitReserve}
                disabled={!reserveCluster}
              >
                Reserve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Release Confirmation Modal ── */}
      {releaseConfirm && (
        <div className="modal-overlay" onClick={() => setReleaseConfirm(null)}>
          <div className="edge-reserve-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Release</h3>

            {releaseConfirm.activeAddrs === null ? (
              <p className="edge-release-warning">
                Could not verify connection status for the selected addresses.
                Do you still want to proceed with the release?
              </p>
            ) : (
              <div className="edge-release-warning">
                <p>
                  The following address{releaseConfirm.activeAddrs.length !== 1 ? 'es have' : ' has'} an
                  active LB tunnel connection:
                </p>
                <ul className="edge-release-active-list">
                  {releaseConfirm.activeAddrs.map((addr) => (
                    <li key={addr}>{addr}</li>
                  ))}
                </ul>
                <p>
                  Releasing {releaseConfirm.activeAddrs.length === 1 ? 'this address' : 'these addresses'} will
                  disconnect the edge node{releaseConfirm.activeAddrs.length !== 1 ? 's' : ''}. Are you sure?
                </p>
              </div>
            )}

            <div className="edge-reserve-actions">
              <button className="edge-btn" onClick={() => setReleaseConfirm(null)}>
                Cancel
              </button>
              <button
                className="edge-btn danger"
                onClick={() => {
                  const { customer, cloud, addresses } = releaseConfirm;
                  setReleaseConfirm(null);
                  doRelease(customer, cloud, addresses);
                }}
              >
                <Trash2 size={13} /> Release Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EdgeCredentials;
